import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Collapse, Nav, NavItem } from "reactstrap";
import SimpleBar from "simplebar-react";
import { useFilteredAppMenuGroups } from "hooks/useFilteredAppMenuGroups";
import { useSelector } from "react-redux";
import OverrideMasterSettingsModal from "pages/AllCommonModal/OverrideMasterSettingsModal/override-master-settings-modal";
import TestEmailServerModal from "pages/AllCommonModal/TestEmailServerModal/test-email-server-modal";
import CreateAuditModal from "pages/AllCommonModal/CreateAuditModal/create-audit";

function normalizePath(pathname = "") {
  return pathname.replace(/^\/+/, "").replace(/\/+$/, "");
}

function pathMatchesRoute(pathname, routeLink) {
  const p = normalizePath(pathname);
  const r = normalizePath(routeLink);
  if (!r) return false;
  return p === r || p.startsWith(`${r}/`);
}

const SidebarContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const leftSideBarType = useSelector((state) => state.Layout?.leftSideBarType);

  const [openGroup, setOpenGroup] = useState(null);
  const [openSubGroup, setOpenSubGroup] = useState(null);

  const [collapsedRail, setCollapsedRail] = useState(false);
  const [flyoutGroupKey, setFlyoutGroupKey] = useState(null);
  const flyoutCloseTimer = useRef(null);
  const flyoutPanelRef = useRef(null);
  const flyoutAnchorRef = useRef(null);

  const [overrideMasterSettingsModalOpen, setOverrideMasterSettingsModalOpen] = useState(false);
  const [testEmailServerModalOpen, setTestEmailServerModalOpen] = useState(false);
  const [createAuditModalOpen, setCreateAuditModalOpen] = useState(false);

  const [activeItem, setActiveItem] = useState({
    main: null,
    master: null,
    client: null,
    candidate: null,
    report: null,
    settings: null,
    incentive: null,
    audit: null,
    team: null,
    call: null,
  });

  const toggleGroup = (group) => {
    setOpenGroup(openGroup === group ? null : group);
  };

  const toggleSubGroup = (key) => {
    setOpenSubGroup(openSubGroup === key ? null : key);
  };

  const clearFlyoutCloseTimer = () => {
    if (flyoutCloseTimer.current) {
      clearTimeout(flyoutCloseTimer.current);
      flyoutCloseTimer.current = null;
    }
  };

  const scheduleFlyoutClose = () => {
    clearFlyoutCloseTimer();
    flyoutCloseTimer.current = setTimeout(() => setFlyoutGroupKey(null), 260);
  };

  const placeCollapsedFlyout = useCallback(() => {
    const flyoutEl = flyoutPanelRef.current;
    const anchorEl = flyoutAnchorRef.current;
    if (!flyoutEl || !anchorEl || typeof window === "undefined") return;

    const anchorRect = anchorEl.getBoundingClientRect();
    const gap = 6;
    const viewportPad = 8;
    const preferredWidth = Math.min(288, Math.max(236, window.innerWidth - viewportPad * 2));

    flyoutEl.classList.add("sidebar-collapsed-flyout--fixed");
    flyoutEl.style.position = "fixed";
    flyoutEl.style.left = `${Math.round(anchorRect.right + gap)}px`;
    flyoutEl.style.right = "auto";
    flyoutEl.style.marginLeft = "0px";
    flyoutEl.style.width = `${preferredWidth}px`;
    flyoutEl.style.maxWidth = `${preferredWidth}px`;
    flyoutEl.style.top = `${Math.round(anchorRect.top)}px`;
    flyoutEl.style.bottom = "auto";
    flyoutEl.style.maxHeight = `${Math.max(
      160,
      window.innerHeight - viewportPad * 2
    )}px`;

    // Measure after applying width/maxHeight, then flip/clamp into viewport.
    const flyoutHeight = Math.min(
      flyoutEl.scrollHeight,
      window.innerHeight - viewportPad * 2
    );
    const spaceBelow = window.innerHeight - anchorRect.top - viewportPad;
    const spaceAbove = anchorRect.bottom - viewportPad;
    const openUpward = spaceBelow < flyoutHeight && spaceAbove > spaceBelow;

    let top = openUpward
      ? anchorRect.bottom - flyoutHeight
      : anchorRect.top;

    top = Math.max(
      viewportPad,
      Math.min(top, window.innerHeight - viewportPad - flyoutHeight)
    );

    const maxHeight = openUpward
      ? Math.max(160, Math.min(flyoutHeight, spaceAbove))
      : Math.max(160, Math.min(window.innerHeight - top - viewportPad, 560));

    flyoutEl.classList.toggle("sidebar-collapsed-flyout--up", openUpward);
    flyoutEl.style.top = `${Math.round(top)}px`;
    flyoutEl.style.maxHeight = `${Math.round(maxHeight)}px`;
  }, []);

  const readCollapsedRail = useCallback(() => {
    if (typeof window === "undefined") return false;
    return document.body.classList.contains("vertical-collpsed");
  }, []);

  const isCollapsedRailMode = collapsedRail || leftSideBarType === "condensed";

  /* Start false so remount after horizontal layout still gets "entered collapsed"
     detection and flyout/route sync behaves like first open of vertical sidebar. */
  const prevCollapsedRailModeRef = useRef(false);
  const prevPathnameForFlyoutRef = useRef(location.pathname);
  const prevPathnameForAccordionRef = useRef(location.pathname);
  const prevExpandedModeRef = useRef(!isCollapsedRailMode);

  const menuGroups = useFilteredAppMenuGroups();

  const activeGroupKeyFromRoute = useMemo(() => {
    for (const group of menuGroups) {
      if (!group.items?.length) continue;
      for (const item of group.items) {
        if (!item.permission) continue;
        if (item.children?.length) {
          for (const child of item.children) {
            if (
              child.permission &&
              pathMatchesRoute(location.pathname, child.routeLink)
            ) {
              return group.key;
            }
          }
        } else if (pathMatchesRoute(location.pathname, item.routeLink)) {
          return group.key;
        }
      }
    }
    return null;
  }, [location.pathname, menuGroups]);

  useEffect(() => {
    const sync = () => {
      setCollapsedRail(readCollapsedRail());
    };
    sync();

    const mq = window.matchMedia("(min-width: 992px)");
    const onMq = () => sync();
    mq.addEventListener("change", onMq);

    const observer = new MutationObserver(sync);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => {
      mq.removeEventListener("change", onMq);
      observer.disconnect();
      clearFlyoutCloseTimer();
    };
  }, [readCollapsedRail]);

  /**
   * Hover/click opens flyouts. Route sync runs on navigation only — not on
   * hamburger collapse (fa-bars), so the panel does not stick open until hover.
   */
  useEffect(() => {
    const justEnteredCollapsed =
      !prevCollapsedRailModeRef.current && isCollapsedRailMode;
    const pathChanged =
      prevPathnameForFlyoutRef.current !== location.pathname;

    prevCollapsedRailModeRef.current = isCollapsedRailMode;
    prevPathnameForFlyoutRef.current = location.pathname;

    if (!isCollapsedRailMode) {
      setFlyoutGroupKey(null);
      return;
    }

    if (justEnteredCollapsed) {
      setFlyoutGroupKey(null);
      return;
    }

    if (pathChanged) {
      if (activeGroupKeyFromRoute && activeGroupKeyFromRoute !== "main") {
        setFlyoutGroupKey(activeGroupKeyFromRoute);
      } else {
        setFlyoutGroupKey(null);
      }
    }
  }, [isCollapsedRailMode, location.pathname, activeGroupKeyFromRoute]);

  const handleCollapsedSidebarMouseLeave = () => {
    if (!isCollapsedRailMode) return;
    scheduleFlyoutClose();
  };

  const handleItemClick = (group, item, index) => {
    if (!item.permission) return;

    setActiveItem({
      main: null,
      master: null,
      client: null,
      candidate: null,
      report: null,
      settings: null,
      incentive: null,
      audit: null,
      team: null,
      call: null,
      [group]: index,
    });

    if (item.action === "overrideMasterSettings") {
      setOverrideMasterSettingsModalOpen(true);
      if (isCollapsedRailMode) setFlyoutGroupKey(null);
      return;
    }

    if (item.action === "testEmailServer") {
      setTestEmailServerModalOpen(true);
      if (isCollapsedRailMode) setFlyoutGroupKey(null);
      return;
    }

    if (item.action === "createAudit") {
      setCreateAuditModalOpen(true);
      if (isCollapsedRailMode) setFlyoutGroupKey(null);
      return;
    }

    if (!item.routeLink) return;

    navigate(`/${item.routeLink}`);
    if (isCollapsedRailMode) setFlyoutGroupKey(null);
  };

  const flyoutLinkClass = (routeLink) => {
    const active = pathMatchesRoute(location.pathname, routeLink);
    return `sidebar-flyout-link${active ? " sidebar-flyout-link--active" : ""}`;
  };

  const renderFlyoutLinks = (groupTitle, groupKey, items, groupIcon) => {
    const flyout = (
      <div
        ref={flyoutPanelRef}
        className="sidebar-collapsed-flyout sidebar-collapsed-flyout--fixed"
        onMouseEnter={clearFlyoutCloseTimer}
        onMouseLeave={scheduleFlyoutClose}
      >
        <div className="sidebar-flyout-header">
          <span className="sidebar-flyout-header-icon" aria-hidden>
            <i className={`${groupIcon || "fa fa-folder"} icon-font-sz`} />
          </span>
          <div className="sidebar-flyout-header-main">
            <span className="sidebar-flyout-title">{groupTitle}</span>
          </div>
          <i className="bx bx-chevron-down sidebar-flyout-chevron" aria-hidden />
        </div>
        <div className="sidebar-flyout-body">
          {items.map((item, index) => {
            if (!item.permission) return null;
            if (item.children?.length) {
              return (
                <div key={item.key || item.name} className="sidebar-flyout-section">
                  <div className="sidebar-flyout-section-title">{item.name}</div>
                  {item.children.map((child, cIndex) =>
                    child.permission ? (
                      <Link
                        key={child.routeLink}
                        to={`/${child.routeLink}`}
                        className={flyoutLinkClass(child.routeLink)}
                        onClick={() => {
                          handleItemClick(groupKey, child, `${index}-${cIndex}`);
                        }}
                      >
                        {child.name}
                      </Link>
                    ) : null
                  )}
                </div>
              );
            }
            if (!item.routeLink) {
              return (
                <button
                  key={item.action || item.name}
                  type="button"
                  className="sidebar-flyout-link border-0 bg-transparent text-start w-100"
                  onClick={() => handleItemClick(groupKey, item, index)}
                >
                  {item.name}
                </button>
              );
            }

            return (
              <Link
                key={item.routeLink}
                to={`/${item.routeLink}`}
                className={flyoutLinkClass(item.routeLink)}
                onClick={() => handleItemClick(groupKey, item, index)}
              >
                {item.name}
              </Link>
            );
          })}
        </div>
      </div>
    );

    if (typeof document === "undefined") return flyout;
    return createPortal(flyout, document.body);
  };

  useEffect(() => {
    if (!flyoutGroupKey || !isCollapsedRailMode) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      placeCollapsedFlyout();
    });

    const handleReposition = () => placeCollapsedFlyout();
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [flyoutGroupKey, isCollapsedRailMode, placeCollapsedFlyout, menuGroups]);

  useEffect(() => {
    const pathChanged = prevPathnameForAccordionRef.current !== location.pathname;
    const justEnteredExpanded = isCollapsedRailMode === false && prevExpandedModeRef.current === false;

    prevPathnameForAccordionRef.current = location.pathname;
    prevExpandedModeRef.current = !isCollapsedRailMode;

    if (isCollapsedRailMode || (!pathChanged && !justEnteredExpanded)) {
      return;
    }

    const currentPath = normalizePath(location.pathname);
    let nextOpenGroup = null;
    let nextOpenSubGroup = null;
    let nextActiveValue = null;

    for (const group of menuGroups) {
      for (const [index, item] of (group.items || []).entries()) {
        if (!item.children?.length && normalizePath(item.routeLink) === currentPath) {
          nextOpenGroup = group.key;
          nextActiveValue = index;
          break;
        }

        for (const [cIndex, child] of (item.children || []).entries()) {
          if (normalizePath(child.routeLink) === currentPath) {
            nextOpenGroup = group.key;
            nextOpenSubGroup = item.key;
            nextActiveValue = `${index}-${cIndex}`;
            break;
          }
        }

        if (nextOpenGroup) break;
      }
      if (nextOpenGroup) break;
    }

    if (!nextOpenGroup) return;

    setOpenGroup(nextOpenGroup);
    setOpenSubGroup(nextOpenSubGroup);
    setActiveItem((prev) => ({
      ...prev,
      [nextOpenGroup]: nextActiveValue,
    }));
  }, [location.pathname, menuGroups, isCollapsedRailMode]);

  return (
    <div
      className={`sidebar bg-dark-blue text-white${
        isCollapsedRailMode ? " sidebar-collapsed-rail" : ""
      }`}
      onMouseLeave={
        isCollapsedRailMode ? handleCollapsedSidebarMouseLeave : undefined
      }
    >
      <SimpleBar
        className="sidebar-scroll simplebar-move-bottom"
        autoHide={false}
        style={{ flex: 1, minHeight: 0, maxHeight: "100%" }}
      >
        <Nav vertical className="p-3">
          {menuGroups.map((group, gIndex) => (
            <NavItem key={gIndex}>
              {group.key === "main" ? (
                group.items.map((item, index) => (
                  <NavItem key={index}>
                    <Link
                      to={`/${item.routeLink}`}
                      className="nav-link text-white d-flex align-items-center level-font-sz"
                      onClick={() => handleItemClick("main", item, index)}
                    >
                      <i className={`${item.icon} me-2 icon-font-sz`} />
                      <span className="sidebar-link-label">{item.name}</span>
                    </Link>
                  </NavItem>
                ))
              ) : isCollapsedRailMode ? (
                <div
                  ref={
                    flyoutGroupKey === group.key
                      ? flyoutAnchorRef
                      : undefined
                  }
                  className={`sidebar-group-rail position-relative ${
                    flyoutGroupKey === group.key ? "is-flyout-open" : ""
                  }`}
                  onMouseEnter={() => {
                    clearFlyoutCloseTimer();
                    setOpenGroup(group.key);
                    setFlyoutGroupKey(group.key);
                  }}
                  onMouseLeave={scheduleFlyoutClose}
                >
                  <button
                    type="button"
                    className={`nav-link w-100 d-flex align-items-center justify-content-center text-white level-font-sz sidebar-rail-trigger bg-transparent border-0 p-2 ${
                      activeGroupKeyFromRoute === group.key ||
                      flyoutGroupKey === group.key
                        ? "sidebar-rail-active"
                        : ""
                    }`}
                    aria-expanded={flyoutGroupKey === group.key}
                    aria-label={group.title}
                    title={group.title}
                    onClick={() =>
                      setFlyoutGroupKey((k) =>
                        k === group.key ? null : group.key
                      )
                    }
                    onMouseEnter={() => {
                      clearFlyoutCloseTimer();
                      setOpenGroup(group.key);
                      setFlyoutGroupKey(group.key);
                    }}
                  >
                    <i className={`${group.icon} icon-font-sz`} />
                  </button>
                  {flyoutGroupKey === group.key
                    ? renderFlyoutLinks(
                        group.title,
                        group.key,
                        group.items,
                        group.icon
                      )
                    : null}
                </div>
              ) : (
                <>
                  <div
                    className="nav-link d-flex justify-content-between align-items-center text-white level-font-sz cursor-pointer"
                    onClick={() => toggleGroup(group.key)}
                  >
                    <div className="d-flex align-items-center">
                      <i className={`${group.icon} me-2 icon-font-sz`} />
                      <span className="sidebar-link-label">{group.title}</span>
                    </div>
                    <i
                      className={`bx ${
                        openGroup === group.key ? "bx-chevron-up" : "bx-chevron-down"
                      }`}
                    />
                  </div>

                  <Collapse isOpen={openGroup === group.key}>
                    <div className="timeline-container">
                      <div className="timeline-line timeline-bar-move-left"></div>
                      <div className="timeline-line"></div>

                      {group.items.map((item, index) => (
                        <div key={index}>
                          {!item.children ? (
                            <div
                              className={`timeline-node ${
                                activeItem[group.key] === index ? "active" : ""
                              }`}
                              onClick={() => handleItemClick(group.key, item, index)}
                            >
                              {item.routeLink ? (
                                <Link to={`/${item.routeLink}`} className="timeline-label">
                                  {item.name}
                                </Link>
                              ) : (
                                <span className="timeline-label">{item.name}</span>
                              )}
                            </div>
                          ) : (
                            <>
                              <div
                                className="timeline-node cursor-pointer"
                                onClick={() => toggleSubGroup(item.key)}
                              >
                                <span className="timeline-label d-flex justify-content-between align-items-center">
                                  {item.name}
                                  <i
                                    className={`bx ${
                                      openSubGroup === item.key
                                        ? "bx-chevron-up"
                                        : "bx-chevron-down"
                                    }`}
                                  />
                                </span>
                              </div>

                              <Collapse isOpen={openSubGroup === item.key}>
                                <div style={{ marginLeft: "18px" }}>
                                  {item.children.map((child, cIndex) => (
                                    <div
                                      key={cIndex}
                                      className={`timeline-node ${
                                        activeItem[group.key] === `${index}-${cIndex}`
                                          ? "active"
                                          : ""
                                      }`}
                                      style={{ paddingLeft: "16px" }}
                                      onClick={() =>
                                        handleItemClick(
                                          group.key,
                                          child,
                                          `${index}-${cIndex}`
                                        )
                                      }
                                    >
                                      <Link
                                        to={`/${child.routeLink}`}
                                        className="timeline-label"
                                      >
                                        {child.name}
                                      </Link>
                                    </div>
                                  ))}
                                </div>
                              </Collapse>
                            </>
                          )}
                        </div>
                      ))}
                    </div>
                  </Collapse>
                </>
              )}
            </NavItem>
          ))}
        </Nav>
      </SimpleBar>

      <OverrideMasterSettingsModal
        overrideMasterSettingsModalOpen={overrideMasterSettingsModalOpen}
        setOverrideMasterSettingsModalOpen={setOverrideMasterSettingsModalOpen}
      />
      <TestEmailServerModal
        testEmailServerModalOpen={testEmailServerModalOpen}
        setTestEmailServerModalOpen={setTestEmailServerModalOpen}
      />
      <CreateAuditModal
        createAuditModalOpen={createAuditModalOpen}
        setCreateAuditModalOpen={setCreateAuditModalOpen}
      />
    </div>
  );
};

export default SidebarContent;
